import React, { useEffect, useState } from 'react';
import { getAllProductApi, getAllCategoryApi, getAllSubcategoriesApi } from '../../../Apis/Api';

const TimeFrame = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState({});
  const [subCategories, setSubCategories] = useState({});
  const [categorizedProducts, setCategorizedProducts] = useState({ 
    red: [], 
    yellow: [], 
    safe: [] 
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productResponse, categoryResponse, subCategoryResponse] = await Promise.all([
          getAllProductApi(),
          getAllCategoryApi(),
          getAllSubcategoriesApi()
        ]);

        const categoryMap = categoryResponse.data.categories.reduce((map, category) => {
          map[category._id] = category.name;
          return map;
        }, {});

        const subCategoryMap = subCategoryResponse.data.subCategories.reduce((map, subCategory) => {
          map[subCategory._id] = subCategory.name;
          return map;
        }, {});

        setCategories(categoryMap);
        setSubCategories(subCategoryMap);
        setProducts(productResponse.data.products);
        categorizeProducts(productResponse.data.products);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateMonthsInInventory = (product) => {
    const now = new Date();
    // Use updatedAt if available, otherwise use createdAt
    const referenceDate = product.updatedAt ? new Date(product.updatedAt) : new Date(product.createdAt);
    return (now.getFullYear() - referenceDate.getFullYear()) * 12 + 
           (now.getMonth() - referenceDate.getMonth());
  };

  const categorizeProducts = (products) => {
    const red = [], yellow = [], safe = [];
  
    products.forEach((product) => {
      const monthsInInventory = calculateMonthsInInventory(product);
  
      if (monthsInInventory >= 6) {  
        red.push(product);
      } else if (monthsInInventory >= 3) { 
        yellow.push(product);
      } else { 
        safe.push(product);
      }
    });
  
    setCategorizedProducts({ red, yellow, safe });
  };

  const getTimeStatusMessage = (product, zone) => {
    const monthsInInventory = calculateMonthsInInventory(product);
    
    switch(zone) {
      case 'safe':
        const monthsLeftToYellow = 3 - monthsInInventory;
        return monthsLeftToYellow > 0 
          ? `Safe for ${monthsLeftToYellow} more month(s)` 
          : 'Ready to move to Yellow Zone';
      
      case 'yellow':
        const monthsLeftToRed = 6 - monthsInInventory;
        return monthsLeftToRed > 0 
          ? `${monthsLeftToRed} month(s) until Red Zone` 
          : 'Ready to move to Red Zone';
      
      case 'red':
        const monthsInRed = monthsInInventory - 6;
        return `In Red Zone for ${monthsInRed} month(s)`;
      
      default:
        return '';
    }
  };

  const sortProductsByTime = (products, zone) => {
    return [...products].sort((a, b) => {
      const aMonths = calculateMonthsInInventory(a);
      const bMonths = calculateMonthsInInventory(b);
      
      // For safe zone, sort by ascending (closest to yellow first)
      if (zone === 'safe') return aMonths - bMonths;
      // For other zones, sort by descending (most critical first)
      return bMonths - aMonths;
    });
  };

  const SkeletonLoader = () => (
    <div className="p-2 bg-gray-300 animate-pulse rounded-md mb-2">
      <div className="h-4 bg-gray-400 rounded w-3/4 mb-2"></div>
      <div className="h-3 bg-gray-400 rounded w-1/2 mb-2"></div>
      <div className="h-3 bg-gray-400 rounded w-1/3 mb-2"></div>
      <div className="h-3 bg-gray-400 rounded w-3/4"></div>
    </div>
  );

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Product Inventory Status</h2>

      <div className="flex space-x-4 overflow-x-auto pb-4">
        {['red', 'yellow', 'safe'].map((zone) => (
          <div
            key={zone}
            className={`flex-1 min-w-[300px] relative rounded-lg overflow-hidden`}
          >
            {/* Zone Title */}
            <div className={`absolute top-0 left-0 w-full text-center py-2 text-white font-semibold 
              ${zone === 'red' ? 'bg-red-600' : zone === 'yellow' ? 'bg-yellow-500' : 'bg-green-600'}`}
            >
              {zone.toUpperCase()} ZONE ({categorizedProducts[zone].length} items)
            </div>

            {/* Zone Content */}
            <div className={`bg-opacity-50 p-4 rounded-lg min-h-[200px] shadow mt-12
              ${zone === 'red' ? 'bg-red-100' : zone === 'yellow' ? 'bg-yellow-100' : 'bg-green-100'}`}
            >
              {loading ? (
                Array(3).fill(0).map((_, index) => <SkeletonLoader key={index} />)
              ) : (
                sortProductsByTime(categorizedProducts[zone], zone).map((product) => (
                  <div key={product._id} className="p-3 bg-white rounded-md shadow mb-3">
                    <h4 className="text-md font-semibold mb-1">{product.name}</h4>
                    <div className="grid grid-cols-2 gap-1 text-sm">
                      <div>
                        <p>Quantity: {product.totalQuantity}</p>
                        <p>Added: {new Date(product.createdAt).toLocaleDateString()}</p>
                        {product.updatedAt && (
                          <p>Updated: {new Date(product.updatedAt).toLocaleDateString()}</p>
                        )}
                      </div>
                      <div>
                        <p>Category: {categories[product.category] || 'Unknown'}</p>
                        <p>Subcategory: {subCategories[product.subcategory] || 'Unknown'}</p>
                      </div>
                    </div>
                    
                    {product.sizes.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium">Sizes:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {product.sizes.map((size) => (
                            <span 
                              key={size._id} 
                              className="text-xs bg-gray-200 px-2 py-1 rounded"
                            >
                              {size.size}: {size.totalSizeQuantity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className={`mt-2 text-sm font-medium ${
                      zone === 'red' ? 'text-red-600' : 
                      zone === 'yellow' ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {getTimeStatusMessage(product, zone)}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimeFrame;